/** @jsx jsx */
import { ClassNames, css, jsx } from '@emotion/react';

import * as React from 'react';

import { MODULE_LAYOUTS, OverflowCarousel, TooltipWrapper, ValuePropsModule } from '@coursera-web/cds-labs';
import type { ValuePropItem } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';

import type { CareerOutcomesFragmentFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import RolesCarousel from 'bundles/degree-description/components/modules/career-outcomes/RolesCarousel';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';

import _t from 'i18n!nls/degree-description';

export type CareerRole = {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
};

const EMPLOYER_LOGO_MAX_WIDTH = 120;
const EMPLOYER_LOGO_MAX_HEIGHT = 67;

const styles = {
  module: css`
    padding-top: var(--cds-spacing-400);
    padding-bottom: var(--cds-spacing-400);
  `,
  zeroPaddingBottom: css`
    padding-bottom: 0 !important;
  `,
  moduleHeading: css`
    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  rolesHeading: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  employerLogosHeading: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  valuePropCard: css`
    ${breakpoints.up('sm')} {
      &:first-of-type {
        padding-left: 0;
      }

      &:last-of-type {
        padding-right: 0;
      }
    }

    ${breakpoints.down('xs')} {
      padding-left: 0;
      padding-right: 0;
    }
  `,
  employerLogos: css`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    align-items: center;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-300);
    }
  `,
  employerLogoWrapper: css`
    max-width: ${EMPLOYER_LOGO_MAX_WIDTH}px;
    max-height: ${EMPLOYER_LOGO_MAX_HEIGHT}px;
  `,
};

const ROLES_HEADING_ID = 'roles-carousel-heading';

type CareerOutcomesValueProp = NonNullable<CareerOutcomesFragmentFragment['valuePropsCollection']>['items'][number];
type CareerOutcomesRole = NonNullable<CareerOutcomesFragmentFragment['rolesCollection']>['items'][number];
type CareerOutcomesLogo = NonNullable<
  CareerOutcomesFragmentFragment['whereGraduatesWorkLogosCollection']
>['items'][number];

type Props = {
  heading?: string | null;
  valueProps?: CareerOutcomesValueProp[];
  rolesTitle?: string | null;
  roles?: CareerOutcomesRole[];
  logosTitle?: string | null;
  logos?: CareerOutcomesLogo[];
};

const CareerOutcomesModule = ({ heading, valueProps, rolesTitle, roles, logosTitle, logos }: Props) => {
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('career_outcomes');

  const mappedValueProps: ValuePropItem[] = (valueProps ?? [])
    .filter((item): item is NonNullable<CareerOutcomesValueProp> => Boolean(item))
    .map((item, index) => ({
      id: `career-value-prop-${index}`,
      headline: item.title,
      description: item.description,
    }));

  const mappedRoles: CareerRole[] = (roles ?? [])
    .filter((item): item is NonNullable<CareerOutcomesRole> => Boolean(item))
    .map((item, index) => ({
      id: `career-role-${index}`,
      title: item.title ?? '',
      description: item.description ?? '',
      imageUrl: item.visual?.url ?? undefined,
    }));

  const filteredLogos = (logos ?? [])
    .map((item) => ({
      url: item?.visual?.url,
      alt: item?.visualAlternativeText ?? undefined,
    }))
    .filter((item): item is { url: string; alt: string | undefined } => Boolean(item.url));

  const resolvedRolesTitle = rolesTitle || _t('Roles after graduating');
  const resolvedLogosTitle = logosTitle || _t('Where our graduates work');
  const headingOrValuePropsArePresent = Boolean(heading) || mappedValueProps.length > 0;

  return (
    <div ref={pageSectionRef} css={styles.module}>
      <Container>
        <Grid container direction="column" spacing={32}>
          {headingOrValuePropsArePresent && (
            <Grid item xs={12} css={styles.zeroPaddingBottom}>
              {heading && (
                <Typography2 variant="subtitleLarge" component="h2" css={styles.moduleHeading}>
                  {heading}
                </Typography2>
              )}
              {mappedValueProps.length > 0 && (
                <ClassNames>
                  {({ css: cx }) => (
                    <ValuePropsModule
                      valueProps={mappedValueProps}
                      layout={MODULE_LAYOUTS.textTopImageBottom}
                      cardBackgroundColor="transparent"
                      isPageGridContainerAppliedOnPage
                      cardClassName={cx(styles.valuePropCard)}
                    />
                  )}
                </ClassNames>
              )}
            </Grid>
          )}
          {mappedRoles.length > 0 && (
            <Grid item xs={12}>
              <section aria-labelledby={ROLES_HEADING_ID}>
                <Typography2 variant="subtitleLarge" component="h3" id={ROLES_HEADING_ID} css={styles.rolesHeading}>
                  {resolvedRolesTitle}
                </Typography2>
                <RolesCarousel roles={mappedRoles} />
              </section>
            </Grid>
          )}
          {filteredLogos.length > 0 && (
            <Grid item xs={12}>
              <Typography2 variant="subtitleLarge" component="h3" css={styles.employerLogosHeading}>
                {resolvedLogosTitle}
              </Typography2>
              <OverflowCarousel css={styles.employerLogos}>
                {filteredLogos.map((item) => (
                  <TooltipWrapper key={item.url} message={item.alt}>
                    <div css={styles.employerLogoWrapper}>
                      <Imgix
                        src={item.url}
                        alt={item.alt ?? ''}
                        maxWidth={EMPLOYER_LOGO_MAX_WIDTH}
                        maxHeight={EMPLOYER_LOGO_MAX_HEIGHT}
                        loading="lazy"
                      />
                    </div>
                  </TooltipWrapper>
                ))}
              </OverflowCarousel>
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(CareerOutcomesModule);
